<script setup>
import { useGlobalStore } from '@/stores/global';
import { ref, nextTick, onMounted, onBeforeUnmount, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';
const props = defineProps({
  tabs: {
    type: Array,
    required: true,
    default: () => [],
  },
  targets: {
    type: Object,
    required: true,
    default: () => {},
  },
});
const globalStore = useGlobalStore();
const isMobile = useMediaQuery('(max-width: 767px)');
const emit = defineEmits(['consult', 'test-drive', 'update:activeId']);
const tabListRef = useTemplateRef('tabList');
const activeId = ref(props.tabs?.[0]?.id || null);

const navEl = ref(null);
function handleApplicationClick(type) {
  globalStore.setApplicationPopup({ visible: true, type });
}
function getTargetEl(id) {
  const t = props.targets;
  return t?.value ? t.value?.[id] : t?.[id];
}

let scrollLock = false;
let lockTimer = null;
async function scrollToTab(id) {
  activeId.value = id;
  emit('update:activeId', id);
  scrollLock = true;
  clearTimeout(lockTimer);

  await nextTick();
  const el = getTargetEl(id);
  if (el) {
    const top = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top, behavior: 'smooth' });
  }
  lockTimer = setTimeout(() => {
    scrollLock = false;
  }, 500);
}

function handleScrollSpy() {
  if (scrollLock) return;
  const list = props.tabs || [];
  if (!list.length) return;

  let nextActive = list[0]?.id ?? null;
  let maxPastTop = -Infinity;

  for (const t of list) {
    const el = getTargetEl(t.id);
    if (!el) continue;
    const top = el.getBoundingClientRect().top;
    if (top <= 1 && top > maxPastTop) {
      maxPastTop = top;
      nextActive = t.id;
    }
  }
  if (nextActive !== activeId.value) {
    activeId.value = nextActive;
    emit('update:activeId', nextActive);
  }
}

const isSticky = ref(false);
function handleSticky() {
  if (!navEl.value) return;
  const header = document.querySelector('header');
  tabListRef.value?.updateInkBar();
  const top = navEl.value.getBoundingClientRect().top;
  isSticky.value = top <= 0.5;

  if (isSticky.value) {
    header.style.position = 'relative';
  } else {
    header.style.position = 'sticky';
  }
}

let rafId = 0;
function onScrollOrResize() {
  if (rafId) return;
  rafId = requestAnimationFrame(() => {
    rafId = 0;
    handleScrollSpy();
    handleSticky();
  });
}

onMounted(() => {
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
  requestAnimationFrame(() => {
    handleScrollSpy();
    handleSticky();
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScrollOrResize);
  window.removeEventListener('resize', onScrollOrResize);
  if (rafId) cancelAnimationFrame(rafId);
  if (lockTimer) clearTimeout(lockTimer);
});

watch(
  () => [props.tabs, props.targets?.value],
  () =>
    requestAnimationFrame(() => {
      handleScrollSpy();
      handleSticky();
    }),
  { deep: true },
);
</script>

<template>
  <div ref="navEl" class="tabs-nav" :class="{ 'tabs-nav--sticky': isSticky }">
    <div class="tabs-nav__inner">
      <Tabs
        v-if="tabs?.length"
        v-model:value="activeId"
        class="tabs-nav__tabs"
        :scrollable="true"
      >
        <TabList ref="tabList" class="tabs-nav__headers">
          <Tab
            v-for="tab in tabs"
            :key="tab.id"
            :value="tab.id"
            class="tabs-nav__title"
            @click="scrollToTab(tab.id)"
          >
            {{ tab.title }}
          </Tab>
        </TabList>
      </Tabs>
      <div v-show="isSticky" class="tabs-nav__ctas">
        <Button
          class="button-black link-normal js-button-tabs-consultation"
          rounded
          @click="handleApplicationClick('consultation')"
        >
          {{ $t('car.consultation') }}
        </Button>
        <Button
          class="p-button-text button-icon-next link-normal js-button-tabs-test-drive"
          rounded
          @click="handleApplicationClick('test_drive')"
        >
          {{ isMobile ? $t('car.test_drive') : $t('car.order_test_drive') }}
          <CIcon
            class="button-icon-prev-icon"
            name="arrow-red"
            width="16"
            height="16"
          />
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tabs-nav {
  $parent: &;
  background-color: $white;
  padding: 0 16px;
  position: sticky;
  top: 0;
  z-index: 24;
  @include media-breakpoint-up(sm) {
    padding: 0 20px;
  }
  @include media-breakpoint-up(md) {
    padding: 0 48px 0 32px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(padding-right, 48);
    @include responsive-spacing(padding-left, 32);
  }
  &__inner {
    align-items: end;
    @include media-breakpoint-up(lg) {
      gap: 4px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 4);
    }
  }

  &__tabs {
    flex: 1 1 auto;
    min-width: 0;
    @include media-breakpoint-up(sm) {
      display: flex;
    }
  }

  &__headers {
    display: flex;
    justify-content: center;
    position: relative;
    &:deep(.p-tablist-content) {
      border-bottom: 1px solid $divider;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
      }
    }
    &:deep(.p-tablist-tab-list) {
      gap: 8px;
      @include media-breakpoint-up(md) {
        justify-content: center;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
    }

    &:deep(.p-tablist-active-bar) {
      height: 4px;
    }
    @include media-breakpoint-up(xl) {
      &:deep(.p-tablist-active-bar) {
        @include responsive-spacing(height, 4);
      }
    }
  }

  &__title {
    @include link-normal;
    padding: 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding, 16);
    }
  }

  &__ctas {
    display: flex;
    gap: 12px;
    justify-content: space-between;
    padding: 16px 0;
    width: 100%;
    @include media-breakpoint-up(md) {
      justify-content: flex-end;
    }
    @include media-breakpoint-up(lg) {
      gap: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 24);
      @include responsive-spacing(gapadding-bottom, 16);
      @include responsive-spacing(gapadding-top, 16);
    }
  }
  &--sticky {
    #{$parent} {
      &__ctas {
        display: flex;
      }
      &__tabs {
        display: none;
      }
    }
    @include media-breakpoint-up(sm) {
      border-bottom: 1px solid $divider;
      #{$parent} {
        &__tabs {
          display: flex;
          margin-bottom: -1px;
        }
        &__inner {
          display: grid;
          gap: 16px;
          grid-template-columns: auto auto;
        }
      }
    }
    @include media-breakpoint-up(md) {
      #{$parent} {
        &__headers {
          &:deep(.p-tablist-content) {
            border-bottom: unset;
          }
          &:deep(.p-tablist-tab-list) {
            justify-content: start;
          }
          &:deep(.p-tablist-active-bar) {
            height: 3px;
          }
        }
        &__title {
          padding-left: 8px;
          padding-right: 8px;
        }
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-bottom-width, 1);
      #{$parent} {
        &__headers {
          &:deep(.p-tablist-active-bar) {
            @include responsive-spacing(height, 3);
          }
        }
        &__title {
          @include responsive-spacing(padding-left, 16);
          @include responsive-spacing(padding-right, 16);
        }
        &__tabs {
          @include responsive-spacing(margin-bottom, -1);
        }
        &__inner {
          @include responsive-spacing(gap, 16);
        }
      }
    }
  }
}
</style>
